import { useMutation, useQuery } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { useContext, useState, useId, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import { Toast } from 'design-system/src/components/Toast/Toast';
import { ErrorMessage } from 'design-system/src/components/ErrorMessage/ErrorMessage';
import { client, OutboundTaskTypeKey, OutboundTaskType, TaskComment, TaskEvent } from '../../../../api';
import type { Task, WithAdditionalTaskResponseFields, ICD10Code } from '../../types';
import { TaskContext } from '../../contexts';
import { RenderHtmlString } from 'design-system/src/components/RenderHtmlString/RenderHtmlString';
import Button from 'design-system/src/components/Button/Button';
import ButtonRow, { Alignment } from 'design-system/src/components/ButtonRow/ButtonRow';
import DeclineModal from '../../DeclineModal';
import NewFollowupTaskModal from '../../NewFollowupTaskModal';
import { ActionCard } from '../../../ActionCard';
import { OtherRequestButton } from '../../actions/shared/OtherRequestButton';
import { sponsoredTaskCopy } from '../../../businessRelationshipNotices/copy';
import { tasksQueryKey } from '../../../../queries';
import { useQueryClient } from '@tanstack/react-query';
import { TaskInstructionSummary, TaskCompletionEventBanner } from '../../shared';
import Typeahead, { TypeaheadOption } from 'design-system/src/components/Typeahead/Typeahead';
import { StructuredTaskViewWrapper } from '../StructuredTaskViewWrapper';
import { FormLabel } from '../../../FormLabel';

/**
 * Dedicated task view for ICD-10 code request (ICD10_CODE_REQUEST).
 */
const Icd10CodeRequestTaskView: React.FC<{
  taskResponse: WithAdditionalTaskResponseFields<Task>;
}> = ({ taskResponse }) => {
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [showNewFollowupTaskModal, setShowNewFollowupTaskModal] = useState(false);
  const [showPrimaryCodeError, setShowPrimaryCodeError] = useState(false);
  const [showDuplicateCodeError, setShowDuplicateCodeError] = useState(false);
  const primaryIcd10LabelId = useId();
  const secondaryIcd10LabelId = useId();

  const { taskId, onTaskUpdate } = useContext(TaskContext);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isTrainingMode = import.meta.env.VITE_TRAINING_MODE === 'true';

  const { drug, patient, prescriber } = taskResponse;
  const task = taskResponse.task;
  const isCompleted = task.status === 'completed';

  // Get completion event for completed tasks
  const completionEvent = useMemo(() => {
    if (!isCompleted || !task.timeline || task.timeline.length === 0) {
      return undefined;
    }
    const lastTimelineItem = task.timeline[task.timeline.length - 1];
    if (lastTimelineItem?.item_type === 'event') {
      return lastTimelineItem as TaskEvent;
    }
    Sentry.captureMessage('No completion event found for ICD-10 code request task', {
      level: 'error',
      extra: {
        taskId,
      },
    });
    return undefined;
  }, [isCompleted, task.timeline, taskId]);

  // Get initial code values from completion event metadata
  const getInitialCode = (field: 'primary_icd10_code' | 'secondary_icd10_code'): string | null => {
    if (!completionEvent?.event_metadata) {
      return null;
    }
    return (completionEvent.event_metadata[field] as string) || null;
  };

  const [selectedPrimaryIcd10Code, setSelectedPrimaryIcd10Code] = useState<string | null>(() =>
    getInitialCode('primary_icd10_code'),
  );
  const [selectedSecondaryIcd10Code, setSelectedSecondaryIcd10Code] = useState<string | null>(() =>
    getInitialCode('secondary_icd10_code'),
  );

  // Fetch ICD-10 codes
  const icd10CodesQuery = useQuery({
    queryKey: ['icd10-codes'],
    queryFn: () => {
      return client.get<{ icd10_codes: ICD10Code[] }>('icd10-codes').then((res) => res.data);
    },
  });

  const handleTaskResponse = useMutation({
    mutationFn: async (data: { primaryCode: string; secondaryCode?: string }) => {
      if (isTrainingMode) {
        console.warn(
          `Training Mode: Mocking task response with ICD-10 codes: primary=${data.primaryCode}, secondary=${data.secondaryCode} for task ID: ${taskId}`,
        );
        return;
      }
      const formData = new FormData();
      formData.append('primary_icd10_code', data.primaryCode);
      if (data.secondaryCode) {
        formData.append('secondary_icd10_code', data.secondaryCode);
      }
      await client.post(`tasks/${taskId}/submit-icd10-code`, formData);
    },
    onSuccess: () => {
      onTaskUpdate();
      toast.custom(() => <Toast variant="success">ICD-10 code submitted successfully.</Toast>);
      navigate('/tasks');
      queryClient.invalidateQueries({ queryKey: tasksQueryKey() });
    },
    onError: (error) => {
      toast.custom(() => <StandardErrorToast />);
      console.error(error);
      Sentry.captureException(error);
    },
  });

  const handleSubmit = () => {
    if (!selectedPrimaryIcd10Code) {
      setShowPrimaryCodeError(true);
      return;
    }

    if (selectedSecondaryIcd10Code && selectedPrimaryIcd10Code === selectedSecondaryIcd10Code) {
      setShowDuplicateCodeError(true);
      return;
    }

    handleTaskResponse.mutate({
      primaryCode: selectedPrimaryIcd10Code,
      secondaryCode: selectedSecondaryIcd10Code || undefined,
    });
  };

  const handlePrimaryCodeChange = (value: string) => {
    setSelectedPrimaryIcd10Code(value);
    setShowPrimaryCodeError(false);
    setShowDuplicateCodeError(false);
  };

  const handleSecondaryCodeChange = (value: string) => {
    setSelectedSecondaryIcd10Code(value);
    setShowDuplicateCodeError(false);
  };

  const initialComment =
    task.timeline && task.timeline.length > 0 && task.timeline[0].item_type === 'comment'
      ? (task.timeline[0] as TaskComment)
      : undefined;

  // Check if completed task has codes to display
  const hasCodesInCompletedTask = useMemo(() => {
    if (!isCompleted || !completionEvent?.event_metadata) {
      return false;
    }
    const metadata = completionEvent.event_metadata as Record<string, unknown>;
    return !!metadata.primary_icd10_code;
  }, [isCompleted, completionEvent]);

  // Determine if we should show the dropdowns
  // Show if: not completed OR (completed AND has codes)
  const shouldShowDropdowns = !isCompleted || hasCodesInCompletedTask;

  // Convert ICD-10 codes to Typeahead options
  let icd10Options: TypeaheadOption[] =
    icd10CodesQuery.data?.icd10_codes.map((code) => ({
      value: code.code,
      description: `${code.code} - ${code.description}`,
    })) || [];

  // For completed tasks, ensure the selected codes are in the options
  // even if the API query hasn't loaded or fails
  if (isCompleted && completionEvent?.event_metadata) {
    const metadata = completionEvent.event_metadata as Record<string, unknown>;
    const completedOptions: TypeaheadOption[] = [];

    if (metadata.primary_icd10_code && metadata.primary_icd10_description) {
      completedOptions.push({
        value: metadata.primary_icd10_code as string,
        description: `${metadata.primary_icd10_code} - ${metadata.primary_icd10_description}`,
      });
    }

    if (metadata.secondary_icd10_code && metadata.secondary_icd10_description) {
      completedOptions.push({
        value: metadata.secondary_icd10_code as string,
        description: `${metadata.secondary_icd10_code} - ${metadata.secondary_icd10_description}`,
      });
    }

    // Merge completed options with API options, preferring completed options
    const allOptions = [...completedOptions];
    icd10CodesQuery.data?.icd10_codes.forEach((code) => {
      if (!allOptions.find((opt) => opt.value === code.code)) {
        allOptions.push({
          value: code.code,
          description: `${code.code} - ${code.description}`,
        });
      }
    });
    icd10Options = allOptions;
  }

  const optionalLabel = (
    <Typography styledAs="bodyExtraSmallSpaceGrotesk" color="var(--grayscale-500)">
      Optional
    </Typography>
  );

  const dropdownConfigs = [
    {
      id: primaryIcd10LabelId,
      label: 'Primary ICD-10 Code',
      value: selectedPrimaryIcd10Code,
      onChange: handlePrimaryCodeChange,
      isOptional: false,
      shouldShow: true,
    },
    {
      id: secondaryIcd10LabelId,
      label: 'Secondary ICD-10 Code',
      value: selectedSecondaryIcd10Code,
      onChange: handleSecondaryCodeChange,
      isOptional: true,
      shouldShow: selectedSecondaryIcd10Code || !isCompleted,
    },
  ];

  return (
    <>
      <StructuredTaskViewWrapper
        taskType={(task.task_title || task.type) as OutboundTaskType}
        generalInfoTableProps={{
          patientName: drug.patient_name,
          prescriberName: drug.prescriber_name,
          dob: drug.dob,
          prescribedOn: drug.prescription_recieved_at,
          drugName: drug.name,
          prescriptionId: drug.id,
        }}
        taskCreatedAt={task.created_at}
        isCompleted={isCompleted}
        showBusinessRelationshipSmallPrint
        sidebar={{
          drug: {
            data: drug,
            showBusinessRelationshipNotice: task.is_pharma_contract_task,
            businessRelationshipNoticeCopy: sponsoredTaskCopy,
          },
          patient: patient ?? undefined,
          prescriber: prescriber ?? undefined,
        }}
      >
        {isCompleted && <TaskCompletionEventBanner completionEvent={completionEvent} />}

        <div className="mb-6 flex flex-col gap-4">
          <TaskInstructionSummary>Select the ICD-10 codes for this request:</TaskInstructionSummary>
          {initialComment && (
            <Typography styledAs="bodyExtraSmallDMSans">
              <RenderHtmlString>{initialComment.message}</RenderHtmlString>
            </Typography>
          )}
        </div>

        {shouldShowDropdowns && (
          <ActionCard>
            {dropdownConfigs.map((dropdown) => {
              if (!dropdown.shouldShow) return null;

              // Find the last visible dropdown
              const visibleDropdowns = dropdownConfigs.filter((d) => d.shouldShow);
              const isLastVisibleDropdown = visibleDropdowns[visibleDropdowns.length - 1].id === dropdown.id;

              return (
                <div key={dropdown.id} className={`w-full ${!isCompleted || !isLastVisibleDropdown ? 'mb-4' : ''}`}>
                  <FormLabel id={dropdown.id}>
                    {dropdown.isOptional ? (
                      <span className="flex items-baseline gap-1.5">
                        {dropdown.label}
                        {optionalLabel}
                      </span>
                    ) : (
                      dropdown.label
                    )}
                  </FormLabel>
                  <Typeahead
                    labelId={dropdown.id}
                    options={icd10Options}
                    onChange={dropdown.onChange}
                    disabled={icd10CodesQuery.isLoading || isCompleted}
                    placeholder={
                      icd10CodesQuery.isLoading ? 'Loading ICD-10 codes...' : 'Search by code or description'
                    }
                    value={dropdown.value || undefined}
                  />
                </div>
              );
            })}
            {!isCompleted && (
              <ButtonRow
                align={Alignment.SpaceBetween}
                fullWidth
                className="border-t-grayscale-100 pt-4 border-t-solid"
              >
                <ButtonRow align={Alignment.Left}>
                  <Button
                    variant="tertiary"
                    size="sm"
                    onClick={() => setShowDeclineModal(true)}
                    disabled={handleTaskResponse.isPending}
                  >
                    Cancel prescription
                  </Button>
                  <OtherRequestButton setShowNewFollowupTaskModal={setShowNewFollowupTaskModal} />
                </ButtonRow>
                <Button
                  size="sm"
                  onClick={handleSubmit}
                  isLoading={handleTaskResponse.isPending}
                  aria-invalid={showPrimaryCodeError || showDuplicateCodeError}
                  aria-describedby={
                    showPrimaryCodeError
                      ? 'primary-code-error'
                      : showDuplicateCodeError
                        ? 'duplicate-code-error'
                        : undefined
                  }
                >
                  Submit
                </Button>
                {showPrimaryCodeError && (
                  <ErrorMessage id="primary-code-error">Please select a primary ICD-10 code</ErrorMessage>
                )}
                {showDuplicateCodeError && (
                  <ErrorMessage id="duplicate-code-error">Primary and secondary codes must be different</ErrorMessage>
                )}
              </ButtonRow>
            )}
          </ActionCard>
        )}
      </StructuredTaskViewWrapper>

      {showDeclineModal && (
        <DeclineModal
          closeModal={() => setShowDeclineModal(false)}
          patient={patient}
          drug={drug}
          taskKey={task.task_key as OutboundTaskTypeKey}
          taskId={taskId}
          onTaskUpdate={onTaskUpdate}
        />
      )}

      {showNewFollowupTaskModal && (
        <NewFollowupTaskModal
          closeModal={() => setShowNewFollowupTaskModal(false)}
          patient={patient}
          drug={drug}
          patientId={patient.id}
          prescriptionId={drug.id}
          taskId={taskId}
          relatedTaskKey={task.task_key as OutboundTaskTypeKey}
          relatedTaskType={task.type as OutboundTaskType}
        />
      )}
    </>
  );
};

export default Icd10CodeRequestTaskView;
